import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ApiService } from '../services/api-service.js';
import { ICON_CDN } from '../icons.js';
import { teknikTokens, teknikButtons } from '../styles/teknik-tokens.js';
import { tkPrimitives } from '../styles/tk-primitives.js';
import '../styles/tk-page.js';

/* ── Add-on definitions ───────────────────────────────────────────────────── */
// A `TIERS` const used to sit above this: three subscription plans, fully
// specified, and never rendered — along with ~110 lines of orphaned .tier-* CSS
// and a monthly/annual toggle that nothing read. All removed. What actually
// ships is donations plus per-instance add-ons.
//
// Add-ons no longer carry a `color` field. Seven decorative hues keyed to
// product identity is how a site ends up speaking seven accents; price is data,
// so it is set in mono instead.

interface AddonDef {
  id: string;
  name: string;
  desc: string;
  icon: string;
  price: number;       // display in cents
  interval: string;    // 'mo' or 'once'
  requiresInstance: boolean;
}

const ADDONS: AddonDef[] = [
  { id: 'addon_cloud_100gb', name: '+100 GB cloud', desc: 'Extra cloud storage on top of your 50 GB.', icon: 'cloud-arrow-up', price: 200, interval: 'mo', requiresInstance: false },
];

interface CartItem {
  product: string;
  instanceId: string;
  customSubdomain: string;
  label: string;
  price: number;
  interval: string;
}

@customElement('app-store')
export class AppStore extends LitElement {
  @state() userInfo: any = null;
  @state() instances: any[] = [];
  @state() loading = true;
  @state() selectedInstance = '';
  @state() purchaseError = '';
  @state() customSubdomain = '';
  @state() userStatus: any = {};
  @state() cart: CartItem[] = [];
  @state() cartOpen = false;
  @state() checkoutLoading = false;
  @state() donateAmount = '';
  @state() donatePreset = 0;
  @state() donateLoading = false;
  @state() donateError = '';
  @state() donateSuccess = false;

  static styles = [
    teknikTokens,
    teknikButtons,
    tkPrimitives,
    css`
      :host { display: block; }

      .icon {
        width: 15px; height: 15px; display: inline-block;
        background: currentColor;
        -webkit-mask-size: contain; mask-size: contain;
        -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;
        -webkit-mask-position: center; mask-position: center;
        flex-shrink: 0;
      }

      /* ── Donate ────────────────────────────────────────────────────────── */
      .donate { padding: 1.6rem; }
      .donate h2 {
        font-family: var(--font-display);
        font-size: 1.35rem; font-weight: 600; margin: 0 0 .5rem;
      }
      .donate p { margin: 0 0 1.25rem; color: var(--tk-muted); line-height: 1.6; max-width: 56ch; }

      .presets { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: .9rem; }
      .preset {
        font-family: var(--font-mono);
        font-variant-numeric: tabular-nums;
        padding: .5rem .95rem;
        border-radius: var(--tk-r-control);
        border: 1px solid var(--tk-border);
        background: transparent;
        color: var(--tk-muted);
        cursor: pointer;
        font-size: .9rem;
        transition: color .15s ease, border-color .15s ease, background .15s ease;
      }
      .preset:hover { color: var(--tk-text); border-color: var(--tk-border-strong); }
      .preset.active {
        background: var(--tk-accent-soft);
        border-color: var(--tk-accent-line);
        color: var(--tk-accent);
      }

      .amount-row { display: flex; align-items: center; gap: .5rem; margin-bottom: 1rem; max-width: 200px; }
      .currency { font-family: var(--font-mono); color: var(--tk-dim); }

      /* ── Add-ons ───────────────────────────────────────────────────────── */
      .instance-row {
        display: flex; align-items: center; gap: .7rem; flex-wrap: wrap;
        margin-bottom: 1rem;
      }
      .instance-row label { font-size: .875rem; color: var(--tk-muted); }
      .instance-row .tk-select { width: auto; min-width: 240px; }

      .addons {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(268px, 1fr));
        gap: 10px;
      }

      .addon {
        display: flex; flex-direction: column; gap: .55rem;
        padding: 1.15rem 1.25rem;
      }
      .addon-top { display: flex; align-items: center; gap: .55rem; }
      .addon-top .icon { color: var(--tk-accent); }
      .addon-name { font-size: .95rem; font-weight: 600; color: var(--tk-text); }
      .addon-desc { font-size: .85rem; color: var(--tk-dim); line-height: 1.5; flex: 1; }

      .addon-foot {
        display: flex; align-items: flex-end; justify-content: space-between;
        gap: .75rem; flex-wrap: wrap; margin-top: .35rem; min-width: 0;
      }
      .price {
        font-family: var(--font-mono);
        font-variant-numeric: tabular-nums;
        font-size: 1.05rem;
        color: var(--tk-text);
      }
      .interval { font-family: var(--font-mono); font-size: .75rem; color: var(--tk-dim); }
      .included { font-size: .75rem; color: var(--tk-dim); margin-top: .25rem; }

      .sub-preview {
        font-family: var(--font-mono); font-size: .75rem;
        color: var(--tk-dim); margin-top: .35rem;
      }

      /* ── Cart ──────────────────────────────────────────────────────────── */
      .cart-fab {
        position: fixed; bottom: 24px; right: 24px; z-index: 850;
        display: inline-flex; align-items: center; gap: .5rem;
        padding: .7rem 1.2rem; border-radius: 9999px;
        background: var(--tk-accent); color: #06110e;
        border: none; cursor: pointer;
        font-family: var(--font-family); font-size: .9rem; font-weight: 600;
        box-shadow: 0 8px 28px rgba(0,0,0,.45);
      }
      .cart-count {
        font-family: var(--font-mono);
        background: rgba(0,0,0,.22); border-radius: 9999px;
        padding: 1px 7px; font-size: .78rem;
      }

      .scrim {
        position: fixed; inset: 0; z-index: 860;
        background: var(--tk-bg);
        opacity: 0; pointer-events: none;
        transition: opacity .2s ease;
        border: 0; padding: 0;
      }
      .scrim.open { opacity: .7; pointer-events: auto; }

      .cart {
        position: fixed; top: 0; right: 0; bottom: 0; z-index: 870;
        width: min(380px, 100%);
        background: var(--tk-panel);
        border-left: 1px solid var(--tk-border);
        transform: translateX(100%);
        transition: transform .22s ease;
        display: flex; flex-direction: column;
        box-sizing: border-box;
      }
      .cart.open { transform: none; }

      .cart-head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 1rem 1.15rem; border-bottom: 1px solid var(--tk-border);
      }
      .cart-title {
        font-family: var(--font-mono); font-size: 11px;
        letter-spacing: .14em; text-transform: uppercase; color: var(--tk-dim);
      }
      .cart-close {
        background: none; border: none; color: var(--tk-muted);
        font-size: 1.4rem; line-height: 1; cursor: pointer; padding: 0 .2rem;
      }
      .cart-close:hover { color: var(--tk-text); }

      .cart-items { flex: 1; overflow-y: auto; padding: .75rem; }
      .cart-item {
        display: flex; align-items: center; gap: .6rem;
        padding: .7rem .8rem; border-bottom: 1px solid var(--tk-border);
        min-width: 0;
      }
      .cart-item:last-child { border-bottom: none; }
      .cart-item-info { flex: 1; min-width: 0; }
      .cart-item-name { font-size: .9rem; font-weight: 500; color: var(--tk-text); }
      .cart-item-meta { font-size: .75rem; color: var(--tk-dim); margin-top: 2px; }
      .cart-item-price {
        font-family: var(--font-mono); font-variant-numeric: tabular-nums;
        font-size: .85rem; color: var(--tk-muted); white-space: nowrap;
      }
      .cart-remove {
        background: none; border: none; color: var(--tk-dim);
        cursor: pointer; font-size: 1.1rem; line-height: 1; padding: 0 .2rem;
      }
      .cart-remove:hover { color: var(--tk-down); }

      .cart-foot { padding: 1rem 1.15rem; border-top: 1px solid var(--tk-border); }
      .cart-total {
        display: flex; align-items: baseline; justify-content: space-between;
        margin-bottom: .9rem;
      }
      .cart-total-label { font-size: .85rem; color: var(--tk-muted); }
      .cart-total-amount {
        font-family: var(--font-mono); font-variant-numeric: tabular-nums;
        font-size: 1.2rem; color: var(--tk-text);
      }

      @media (max-width: 560px) {
        .cart-fab { bottom: 24px; right: 16px; }
      }

      @media (prefers-reduced-motion: reduce) {
        .scrim, .cart { transition: none; }
      }
    `,
  ];

  async firstUpdated() {
    try {
      const auth = await ApiService.getAuthStatus();
      this.userInfo = auth.authenticated ? auth.user : null;
    } catch { this.userInfo = null; }

    const params = new URLSearchParams(window.location.search);
    if (params.get('donation') === 'success') this.donateSuccess = true;

    if (this.userInfo) await Promise.all([this.loadInstances(), this.loadUserStatus()]);
    this.loading = false;
  }

  async loadInstances() {
    if (!this.userInfo) return;
    try {
      const res = await fetch('/api/instances', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        this.instances = data.instances || [];
        if (this.instances.length > 0) this.selectedInstance = this.instances[0].id;
      }
    } catch (_) {}
  }

  async loadUserStatus() {
    if (!this.userInfo) return;
    try {
      const res = await fetch('/api/billing/user-status', { credentials: 'include' });
      if (res.ok) this.userStatus = await res.json();
    } catch (_) {}
  }

  addToCart(productId: string, label: string, price: number, interval: string) {
    if (!this.userInfo) { window.location.href = '/login'; return; }

    const isAccountLevel = ['addon_extra_instance', 'addon_cloud_100gb'].includes(productId);
    if (!isAccountLevel && !this.selectedInstance) {
      this.purchaseError = 'Choose which instance this applies to first.';
      return;
    }

    const instanceId = isAccountLevel ? '' : this.selectedInstance;
    const exists = this.cart.some(c => c.product === productId && c.instanceId === instanceId);
    if (exists) {
      this.removeFromCart(productId, instanceId);
      return;
    }

    this.purchaseError = '';
    this.cart = [...this.cart, {
      product: productId,
      instanceId,
      customSubdomain: '',
      label,
      price,
      interval,
    }];
    this.cartOpen = true;
  }

  removeFromCart(productId: string, instanceId: string) {
    this.cart = this.cart.filter(c => !(c.product === productId && c.instanceId === instanceId));
  }

  isInCart(productId: string): boolean {
    const isAccountLevel = ['addon_extra_instance', 'addon_cloud_100gb'].includes(productId);
    const instanceId = isAccountLevel ? '' : this.selectedInstance;
    return this.cart.some(c => c.product === productId && c.instanceId === instanceId);
  }

  async checkout() {
    if (this.cart.length === 0) return;
    this.checkoutLoading = true;
    this.purchaseError = '';

    try {
      const items = this.cart.map(c => ({
        product: c.product,
        instanceId: c.instanceId || undefined,
        customSubdomain: c.customSubdomain || undefined,
      }));

      const res = await fetch('/api/billing/cart-checkout', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      this.purchaseError = data.error || "Couldn't start checkout. Try again.";
    } catch (e: any) { this.purchaseError = e.message; }
    this.checkoutLoading = false;
  }

  private _getDonateAmountCents(): number {
    if (this.donatePreset > 0) return this.donatePreset;
    const val = parseFloat(this.donateAmount);
    if (!val || val < 1 || val > 500) return 0;
    return Math.round(val * 100);
  }

  async donate() {
    const cents = this._getDonateAmountCents();
    if (!cents) { this.donateError = 'Enter an amount between $1 and $500.'; return; }
    this.donateLoading = true;
    this.donateError = '';
    try {
      const res = await fetch('/api/billing/donate', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: cents }),
      });
      const data = await res.json();
      if (res.ok && data.url) { window.location.href = data.url; return; }
      this.donateError = data.error || "Couldn't start the donation. Try again.";
    } catch (e: any) { this.donateError = e.message; }
    this.donateLoading = false;
  }

  private _fmtPrice(cents: number) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  private _isAddonOwned(addonId: string): boolean {
    const inst = this.instances.find((i: any) => i.id === this.selectedInstance) as any;
    const tier = this.userStatus?.tier;
    const hasMcTier = tier === 'minecraft_op' || tier === 'teknik_vip';

    switch (addonId) {
      case 'addon_permanent':
        if (hasMcTier) return true;
        return !!inst?.permanent;
      case 'addon_subdomain':
        if (hasMcTier) return true;
        return !!inst?.customSubdomain;
      default:
        // The rest stack, so they are never "owned".
        return false;
    }
  }

  private _addonIncludedInTier(addonId: string): string | null {
    const tier = this.userStatus?.tier;
    if (!tier || !this.userStatus?.tierActive) return null;
    const hasMcTier = tier === 'minecraft_op' || tier === 'teknik_vip';
    const hasCloudTier = tier === 'teknik_cloud' || tier === 'teknik_vip';
    const hasVip = tier === 'teknik_vip';

    switch (addonId) {
      case 'addon_permanent': return hasMcTier ? (hasVip ? 'Teknik VIP' : 'Minecraft OP') : null;
      case 'addon_subdomain': return hasMcTier ? (hasVip ? 'Teknik VIP' : 'Minecraft OP') : null;
      case 'addon_cloud_100gb': return hasCloudTier ? (hasVip ? 'Teknik VIP' : 'Teknik Cloud') : null;
    }
    return null;
  }

  private _maskIcon(name: string) {
    return `-webkit-mask-image:url(${ICON_CDN}/${name}.svg);mask-image:url(${ICON_CDN}/${name}.svg)`;
  }

  render() {
    return html`
      <tk-page
        eyebrow="Support and add-ons"
        heading="Store"
        sub="Donate, or add storage to your account."
      >
        ${this.loading
          ? html`<div class="tk-empty"><span class="tk-spinner"></span></div>`
          : html`
              ${this._renderDonate()}

              <div class="tk-rule"><span>Add-ons</span></div>
              ${this._renderAddons()}
            `}
      </tk-page>

      ${this.cart.length > 0
        ? html`<button class="cart-fab" @click=${() => (this.cartOpen = true)}>
            <span class="icon" style=${this._maskIcon('cart3')}></span>
            Cart
            <span class="cart-count">${this.cart.length}</span>
          </button>`
        : ''}

      <button
        class="scrim ${this.cartOpen ? 'open' : ''}"
        aria-label="Close cart"
        @click=${() => (this.cartOpen = false)}
      ></button>

      ${this._renderCart()}
    `;
  }

  private _renderDonate() {
    return html`
      <div class="tk-panel donate">
        <h2>Support Teknik</h2>
        <p>Donations cover server costs and development.</p>

        ${this.donateSuccess
          ? html`<div class="tk-flash ok">Donation received. Thank you.</div>`
          : html`
              <div class="presets">
                ${[200, 500, 1000, 2500].map(amt => html`
                  <button
                    class="preset ${this.donatePreset === amt ? 'active' : ''}"
                    @click=${() => {
                      this.donatePreset = this.donatePreset === amt ? 0 : amt;
                      this.donateAmount = '';
                      this.donateError = '';
                    }}
                  >$${(amt / 100).toFixed(0)}</button>
                `)}
              </div>

              <div class="amount-row">
                <span class="currency">$</span>
                <input
                  class="tk-input"
                  type="number" min="1" max="500" step="any"
                  placeholder="Other"
                  .value=${this.donateAmount}
                  @input=${(e: any) => {
                    this.donateAmount = e.target.value;
                    this.donatePreset = 0;
                    this.donateError = '';
                  }}
                  @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.donate(); }}
                />
              </div>

              ${this.donateError
                ? html`<div class="tk-flash err" style="margin-bottom:.9rem">${this.donateError}</div>`
                : ''}

              <button
                class="tk-btn primary"
                ?disabled=${this.donateLoading || this._getDonateAmountCents() === 0}
                @click=${() => this.donate()}
              >
                ${this.donateLoading
                  ? html`<span class="tk-spinner"></span> Redirecting…`
                  : html`Donate ${this._getDonateAmountCents() > 0 ? this._fmtPrice(this._getDonateAmountCents()) : ''}`}
              </button>
            `}
      </div>
    `;
  }

  private _renderAddons() {
    return html`
      ${this.userInfo && this.instances.length > 0
        ? html`<div class="instance-row">
            <label for="inst">Apply to</label>
            <select
              id="inst"
              class="tk-select"
              @change=${(e: any) => (this.selectedInstance = e.target.value)}
            >
              ${this.instances.map((i: any) => html`
                <option value=${i.id} ?selected=${i.id === this.selectedInstance}>
                  ${i.name} (${i.subdomain}.teknik.sh)
                </option>
              `)}
            </select>
          </div>`
        : ''}

      ${this.purchaseError ? html`<div class="tk-flash err" style="margin-bottom:1rem">${this.purchaseError}</div>` : ''}

      <div class="addons">
        ${ADDONS.map(a => {
          const owned = this._isAddonOwned(a.id);
          const includedIn = this._addonIncludedInTier(a.id);
          return html`
            <div class="tk-panel addon">
              <div class="addon-top">
                <span class="icon" style=${this._maskIcon(a.icon)}></span>
                <span class="addon-name">${a.name}</span>
              </div>
              <div class="addon-desc">${a.desc}</div>

              <div class="addon-foot">
                ${owned
                  ? html`<div>
                      <span class="tk-badge ok">Owned</span>
                      ${includedIn ? html`<div class="included">Included in ${includedIn}</div>` : ''}
                    </div>`
                  : html`
                      <div>
                        <span class="price">${this._fmtPrice(a.price)}</span>
                        <span class="interval">${a.interval === 'mo' ? '/mo' : ' once'}</span>
                        ${includedIn ? html`<div class="included">Included in ${includedIn}</div>` : ''}
                      </div>
                      <button
                        class="tk-btn ${this.isInCart(a.id) ? 'secondary' : 'primary'}"
                        @click=${() => this.addToCart(a.id, a.name, a.price, a.interval)}
                      >
                        ${this.isInCart(a.id) ? 'In cart' : !this.userInfo ? 'Sign in' : 'Add'}
                      </button>
                    `}
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }

  private _renderCart() {
    return html`
      <div class="cart ${this.cartOpen ? 'open' : ''}">
        <div class="cart-head">
          <span class="cart-title">Cart · ${this.cart.length}</span>
          <button class="cart-close" @click=${() => (this.cartOpen = false)} aria-label="Close cart">&times;</button>
        </div>

        <div class="cart-items">
          ${this.cart.length === 0
            ? html`<div class="tk-empty">Your cart is empty.</div>`
            : this.cart.map(item => html`
                <div class="cart-item">
                  <div class="cart-item-info">
                    <div class="cart-item-name">${item.label}</div>
                    <div class="cart-item-meta">
                      ${item.instanceId
                        ? html`${this.instances.find((i: any) => i.id === item.instanceId)?.name || item.instanceId}`
                        : 'Account-wide'}
                      ${item.customSubdomain ? html` · ${item.customSubdomain}.teknik.sh` : ''}
                    </div>
                  </div>
                  <span class="cart-item-price">
                    ${this._fmtPrice(item.price)}${item.interval === 'once' ? '' : `/${item.interval}`}
                  </span>
                  <button
                    class="cart-remove"
                    @click=${() => this.removeFromCart(item.product, item.instanceId)}
                    aria-label="Remove ${item.label}"
                  >&times;</button>
                </div>
              `)}
        </div>

        ${this.cart.length > 0
          ? html`<div class="cart-foot">
              <div class="cart-total">
                <span class="cart-total-label">Total</span>
                <span class="cart-total-amount">
                  ${this._fmtPrice(this.cart.reduce((sum, c) => sum + c.price, 0))}
                </span>
              </div>
              ${this.purchaseError ? html`<div class="tk-flash err" style="margin-bottom:.7rem">${this.purchaseError}</div>` : ''}
              <button
                class="tk-btn primary"
                style="width:100%"
                ?disabled=${this.checkoutLoading}
                @click=${() => this.checkout()}
              >
                ${this.checkoutLoading ? 'Redirecting…' : `Checkout · ${this.cart.length} item${this.cart.length > 1 ? 's' : ''}`}
              </button>
              <button
                class="tk-btn quiet"
                style="width:100%;margin-top:.4rem"
                @click=${() => { this.cart = []; this.cartOpen = false; }}
              >Clear cart</button>
            </div>`
          : ''}
      </div>
    `;
  }
}
